import _ from "lodash"
import { userAPIKit } from "../services/user/api"

const FLAG_KEYS = [
  "isShekelSync",
  "cost_price_flag",
  "purchase_exists_flag",
  "tenantPaymentConfigEnable",
  "platFormAccountFlag",
  "taxProvider",
]

const APPLIED_KEY = "saasOnboardingFlagsTenantKey"
const SNAPSHOT_KEY = "saasTenantOnboardingFlagFallback"

let requestSerial = 0

export const isOnboardingFlagEnabled = (value) => {
  if (value === true || value === 1) return true
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    return ["true", "yes", "y", "1", "enabled"].includes(normalized)
  }
  return false
}

const isSaasSession = () =>
  `${localStorage.getItem("sourceRole") || ""}`.includes("SaaS")

const readSelectedIdentity = () => {
  const realmId = `${localStorage.getItem("selectedDefaultRealmValue") || ""}`.trim()
  const tenantId = `${localStorage.getItem("selectedDefaultTenantId") || ""}`.trim()
  return { realmId, tenantId }
}

const setBooleanFlag = (key, value) => {
  localStorage.setItem(key, isOnboardingFlagEnabled(value) ? "true" : "false")
}

/**
 * Writes the same localStorage flags App.js stores from the logged-in tenant.
 * profit_exists -> cost_price_flag
 * purchase_exists -> purchase_exists_flag
 */
export const applyTenantOnboardingFlags = (tenant = {}) => {
  const shelfTypes = Array.isArray(tenant?.shelfTypes) ? tenant.shelfTypes : []
  const shekelTypeName =
    typeof window !== "undefined" ? window?.getShelfTypes?.SHEKEL_TYPE_NAME : ""
  const hasShekel =
    Boolean(shekelTypeName) &&
    shelfTypes.some((item) => item?.name === shekelTypeName)

  localStorage.setItem("isShekelSync", hasShekel ? "true" : "false")
  setBooleanFlag("cost_price_flag", tenant?.profit_exists)
  setBooleanFlag("purchase_exists_flag", tenant?.purchase_exists)
  setBooleanFlag("tenantPaymentConfigEnable", tenant?.paymentConfigEnable)
  setBooleanFlag("platFormAccountFlag", tenant?.platformAccount?.isDefault)
  localStorage.setItem("taxProvider", tenant?.taxProvider?.name || "")
}

export const snapshotLoginOnboardingFlags = () => {
  const snapshot = {}
  FLAG_KEYS.forEach((key) => {
    snapshot[key] = localStorage.getItem(key)
  })
  localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot))
}

const restoreLoginOnboardingFlags = () => {
  if (!localStorage.getItem(APPLIED_KEY)) return false

  const raw = localStorage.getItem(SNAPSHOT_KEY)
  if (raw) {
    try {
      const snapshot = JSON.parse(raw)
      FLAG_KEYS.forEach((key) => {
        const value = snapshot?.[key]
        if (value === null || value === undefined) {
          localStorage.removeItem(key)
        } else {
          localStorage.setItem(key, value)
        }
      })
    } catch (_error) {
      // Leave the current flags in place when the snapshot cannot be read.
    }
  }

  localStorage.removeItem(APPLIED_KEY)
  return true
}

const readTenantConfig = (response) => {
  const root = _.get(response, "data.data")
  if (!root || typeof root !== "object") return null

  const companyDetail = _.get(root, "companyDetail")
  const tenant = _.get(root, "tenant")
  const source =
    companyDetail && typeof companyDetail === "object"
      ? { ...(tenant && typeof tenant === "object" ? tenant : {}), ...companyDetail }
      : tenant && typeof tenant === "object"
      ? tenant
      : root

  const hasIdentity =
    source?.tenantId ||
    source?.tenantName ||
    source?.profit_exists !== undefined ||
    source?.purchase_exists !== undefined ||
    source?.paymentConfigEnable !== undefined ||
    source?.platformAccount ||
    source?.shelfTypes ||
    source?.taxProvider

  return hasIdentity ? source : null
}

export const syncSaasSelectedTenantOnboardingFlags = async ({
  force = false,
} = {}) => {
  if (!isSaasSession()) return false

  const { realmId, tenantId } = readSelectedIdentity()
  if (!realmId || !tenantId) {
    return restoreLoginOnboardingFlags()
  }

  const identityKey = `${realmId}::${tenantId}`
  if (!force && localStorage.getItem(APPLIED_KEY) === identityKey) {
    return false
  }

  const serial = ++requestSerial
  try {
    const response = await userAPIKit.getTenantDetailWithAdmin(tenantId, {
      realmId,
      tenantId,
    })
    if (serial !== requestSerial) return false

    const current = readSelectedIdentity()
    if (current.realmId !== realmId || current.tenantId !== tenantId) {
      return false
    }

    const config = readTenantConfig(response)
    if (!config) {
      return restoreLoginOnboardingFlags()
    }

    applyTenantOnboardingFlags(config)
    localStorage.setItem(APPLIED_KEY, identityKey)
    return true
  } catch (_error) {
    if (serial !== requestSerial) return false
    return restoreLoginOnboardingFlags()
  }
}
